/**
 * 工具调用详情的数据整理。
 *
 * 对齐客户端 packages/ui/src/lib/tool-parsers.ts 的 extractOverlayCards：
 * 一次调用拆成「Input 卡 + Output 卡」，Input 给命令行预览与参数树，
 * Output 按工具类型分发成 code / terminal / document / json / text。
 *
 * 本项目 WS 只下发 toolName / args / result / meta，所以这里做纯降级：
 * 取不到结构化内容时退化为纯文本，绝不造数据。
 */

import { normalizeToolName } from './processTrace'
import { toolNameOf, getToolDisplayName } from './toolNames'

const READ_TOOLS = ['read_file', 'readSkillFile', 'read_workspace_memory_file', 'readMateClawDoc']
const WRITE_TOOLS = ['write_file', 'edit_file', 'editFile', 'writeFile', 'renderDocx', 'renderDocxFromFile', 'renderDocxFromFiles']
const SHELL_TOOLS = ['execute_shell_command', 'runSkillScript', 'load_skill', 'search_file', 'search_content', 'list_files']
const DOC_TOOLS = ['web_search', 'web_fetch', 'call_llm', 'ask_followup']

/** 尝试把 args 解析成对象；解析不了返回 null。 */
export function parseArgs(args) {
  if (args == null || args === '') return null
  if (typeof args === 'object') return args
  if (typeof args !== 'string') return null
  try {
    const obj = JSON.parse(args)
    return obj && typeof obj === 'object' ? obj : null
  } catch (e) {
    return null
  }
}

/** 路径取扩展名 → 代码块语言。 */
export function languageOf(path) {
  const s = String(path || '')
  const idx = s.lastIndexOf('.')
  const ext = idx === -1 ? '' : s.slice(idx + 1).toLowerCase()
  const map = {
    js: 'javascript',
    ts: 'typescript',
    vue: 'html',
    json: 'json',
    py: 'python',
    md: 'markdown',
    sh: 'bash',
    sql: 'sql',
    yml: 'yaml',
    yaml: 'yaml',
    html: 'html',
    css: 'css',
    scss: 'scss',
    java: 'javascript'
  }
  return map[ext] || 'text'
}

/** 命令行预览：对齐目标 formatToolCommandPreview（去掉 mcp 前缀，按 key 排序拼 --k v）。 */
export function commandPreviewOf(name, args) {
  const obj = parseArgs(args)
  if (!obj) return ''
  if (obj.command) return String(obj.command)
  const skip = ['_intent', 'intent', '_displayName', 'displayName', 'description', 'code']
  const parts = [normalizeToolName(name)]
  Object.keys(obj)
    .filter((k) => skip.indexOf(k) === -1 && obj[k] != null && obj[k] !== '')
    .sort()
    .forEach((k) => {
      const v = obj[k]
      parts.push('--' + k)
      parts.push(typeof v === 'string' ? v : JSON.stringify(v))
    })
  return parts.join(' ')
}

/** 结果文本：去掉 MEDIA: 行（媒体由文件卡片单独渲染）。 */
function resultTextOf(msg) {
  const raw = msg && msg.result
  if (raw == null || raw === '') return ''
  if (typeof raw === 'object') return JSON.stringify(raw, null, 2)
  return String(raw).replace(/^MEDIA:(.+?)(?:\n|$)/gm, '').trim()
}

/** 把一次调用整理成浮层需要的结构。 */
export function buildActivityDetail(msg) {
  if (!msg) return null
  const rawName = toolNameOf(msg)
  const name = normalizeToolName(rawName)
  const obj = parseArgs(msg.args) || {}
  const meta = msg.meta || {}
  const alias = obj._displayName || obj.displayName || meta.displayName
  const title = alias ? String(alias) : getToolDisplayName(rawName)
  const filePath = obj.file_path || obj.path || ''
  const text = resultTextOf(msg)

  let output = null
  if (text) {
    if (READ_TOOLS.indexOf(name) !== -1) {
      output = { kind: 'code', language: languageOf(filePath), text, label: 'Output' }
    } else if (SHELL_TOOLS.indexOf(name) !== -1) {
      output = { kind: 'terminal', text, label: 'Output' }
    } else if (DOC_TOOLS.indexOf(name) !== -1) {
      output = { kind: 'document', text, label: 'Output' }
    } else if (/^\s*[{[]/.test(text)) {
      output = { kind: 'json', text, label: 'Output' }
    } else if (WRITE_TOOLS.indexOf(name) !== -1) {
      output = { kind: 'code', language: languageOf(filePath), text, label: 'Output' }
    } else {
      output = { kind: 'text', text, label: 'Output' }
    }
  }

  return {
    title,
    toolName: name,
    filePath,
    commandPreview: commandPreviewOf(rawName, msg.args),
    params: obj,
    hasParams: Object.keys(obj).length > 0,
    output,
    status: msg.isError ? 'error' : msg.phase === 'result' ? 'completed' : 'running'
  }
}
